<template>
  <div
    v-if="loading || items.length"
    class="keyword-topic-chips"
  >
    <div class="kw-col-head">
      <span>{{ title }}</span>
      <span class="text-right">{{ $t('keywords.list.header.forAiLucy') }}</span>
    </div>
    <v-skeleton-loader
      v-if="loading && !items.length"
      type="list-item@4"
      class="pa-2"
    />
    <div
      v-else
      class="kw-scroll"
    >
      <div
        v-for="item in items"
        :key="item.keyword"
        class="kw-row"
        @click.stop
      >
        <div class="kw-row__mid">
          <div class="kw-row__phrase">{{ item.keyword }}</div>
          <div class="kw-row__meta">
            <span
              v-if="sourceChip"
              class="kw-src"
              :class="'kw-src--' + sourceChip"
            >{{ sourceChipLabel }}</span>
            <span
              v-if="showVolume && (item.searchVolume || item.keywordDifficulty)"
              class="kw-row__vol"
            >
              <template v-if="item.searchVolume">{{ formatNumber(item.searchVolume) }}</template>
              <template v-if="item.keywordDifficulty"> · KD {{ item.keywordDifficulty }}</template>
            </span>
          </div>
        </div>
        <div class="kw-row__controls">
          <KeywordLucyRowControls
            :phrase="item.keyword"
            :keyword-type="keywordType"
            :read-only="readOnly"
            :selected-for-ai="selectedForAiLocal"
            @update:selectedForAi="onLucySelectionUpdate"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
    import Vue, {PropType} from 'vue';
    import {Article, KeywordForAi} from '@/model/Article';
    import {KeywordType} from '@/enum/keyword_type';
    import KeywordLucyRowControls from '@/components/keywords/KeywordLucyRowControls.vue';

    export type TopicChipItem = {
        keyword: string;
        searchVolume?: number | null;
        keywordDifficulty?: number | null;
    };

    export default Vue.extend({
        name: 'KeywordTopicChips',
        components: {KeywordLucyRowControls},
        props: {
            title: {
                type: String,
                required: true
            },
            hint: {
                type: String,
                default: ''
            },
            items: {
                type: Array as PropType<TopicChipItem[]>,
                default: () => []
            },
            keywordType: {
                type: Number,
                default: KeywordType.RELATED
            },
            showVolume: {
                type: Boolean,
                default: false
            },
            showFaq: {
                type: Boolean,
                default: false
            },
            sourceChip: {
                type: String,
                default: ''
            },
            loading: {
                type: Boolean,
                default: false
            },
            readOnly: {
                type: Boolean,
                default: false
            },
            selectedForAi: {
                type: Array as PropType<KeywordForAi[]>,
                default: () => []
            }
        },
        data: () => ({
            selectedForAiLocal: [] as (KeywordForAi | string)[]
        }),
        computed: {
            liveArticle(): Article {
                return this.$store.getters.article;
            },
            sourceChipLabel(): string {
                if (!this.sourceChip) {
                    return '';
                }
                if (this.sourceChip === 'pas') {
                    return 'PAS';
                }
                if (this.sourceChip === 'ac') {
                    return String(this.$t('aiBrief.sources.autocomplete'));
                }
                if (this.sourceChip === 'top') {
                    return String(this.$t('aiBrief.sources.organicFaq'));
                }
                return this.sourceChip;
            }
        },
        watch: {
            selectedForAi: {
                immediate: true,
                handler: function () {
                    this.selectedForAiLocal = this.selectedForAi;
                }
            },
            selectedForAiLocal: function () {
                this.$emit('update:selectedForAi', this.selectedForAiLocal);
            }
        },
        methods: {
            formatNumber(value: number): string {
                return Number(value).toLocaleString(this.$i18n.locale);
            },
            onLucySelectionUpdate(rows: KeywordForAi[]): void {
                this.selectedForAiLocal = rows;
            },
        }
    });
</script>

<style scoped lang="sass">
.keyword-topic-chips
    width: 100%

.kw-col-head
    display: grid
    grid-template-columns: minmax(0, 1fr) auto
    gap: 8px
    padding: 6px 10px
    font-size: 10px
    font-weight: 800
    letter-spacing: .04em
    text-transform: uppercase
    color: rgba(255, 255, 255, .85)
    background: linear-gradient(120deg, #0c98f5 0%, #0566c3 100%)

.kw-scroll
    max-height: 430px
    overflow-y: auto

.kw-row
    display: grid
    grid-template-columns: minmax(0, 1fr) auto
    gap: 8px
    align-items: start
    padding: 8px 10px
    border-bottom: 1px solid #edf0f5

.kw-row__lucy
    display: flex
    align-items: center
    gap: 4px
    font-size: 10px
    font-weight: 700
    color: #6b7385

.kw-row__lucy-box ::v-deep .v-input--selection-controls__input
    margin-right: 4px

.kw-row__lucy-lbl
    font-size: 10px
    font-weight: 700
    line-height: 1.2
    letter-spacing: .02em
    color: #6b7385

.kw-row__mid
    min-width: 0

.kw-row__phrase
    font-size: 13px
    font-weight: 500
    line-height: 1.35

.kw-row__meta
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 4px
    margin-top: 4px

.kw-src
    display: inline-flex
    align-items: center
    height: 18px
    padding: 0 6px
    border-radius: 999px
    font-size: 10px
    font-weight: 800
    border: 1px solid #d5dce8
    color: #4a5368
    background: #fff

.kw-src--ac
    border-color: #c9d4c0
    background: #f4f8ee

.kw-src--top
    border-color: #cfd6e4

.kw-src--pas
    color: var(--sw-primary-fill)
    border-color: #b7d4c8
    background: #eef7f2

.kw-row__vol
    font-size: 11px
    color: #6b7385

.kw-row__pills
    display: flex
    gap: 4px
    flex-wrap: wrap
    justify-content: flex-end

.kw-pill
    height: 22px !important
    min-width: 74px
    padding: 0 8px !important
    border-radius: 4px !important
    font-size: 11px !important
    font-weight: 700 !important

.kw-pill--h2
    background: #e8f4fc !important
    color: #0566c3 !important

.kw-pill--faq
    background: #fff !important
    color: #8e3d82 !important
    box-shadow: inset 0 0 0 1px #cd7ec2 !important

.kw-pill--on
    background: #6eaa34 !important
    color: var(--sw-on-accent) !important
    box-shadow: none !important

@media (max-width: 959px)
    .kw-col-head,
    .kw-row
        grid-template-columns: 28px minmax(0, 1fr)

    .kw-row__lucy-lbl
        display: none

    .kw-row__pills
        grid-column: 2
        justify-content: flex-start
</style>
